import { useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@stores/authStore'
import { api } from '@/lib/api'

// Pages
import LandingPage from '@pages/LandingPage'
import LoginPage from '@pages/LoginPage'
import ProjectsPage from '@pages/ProjectsPage'
import WorkspacePage from '@pages/WorkspacePage'
import AnalyticsPage from '@pages/AnalyticsPage'

// Layout
import Header from '@components/layout/Header'
import Footer from '@components/layout/Footer'

/**
 * Validates the persisted JWT against the backend `/api/auth/me`
 * endpoint on mount. If the token is missing, expired, or references a
 * deleted user, the auth store is cleared and the user is bounced to
 * `/login`.
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, token, logout } = useAuthStore()
  const [validating, setValidating] = useState(true)
  const validatedRef = useRef(false)

  useEffect(() => {
    if (validatedRef.current) {
      setValidating(false)
      return
    }
    validatedRef.current = true

    if (!token) {
      setValidating(false)
      return
    }
    let cancelled = false
    void (async () => {
      const response = await api.auth.me()
      if (cancelled) return
      // 401 from the backend triggers logout via the api interceptor.
      // We additionally clear here for any other failure mode so a stale
      // session can't render protected pages.
      if (!response.success) {
        logout()
      }
      setValidating(false)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (validating) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-slate-500">
        Validating session…
      </div>
    )
  }
  return <>{children}</>
}

// Public Route wrapper (redirects to projects if already logged in)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore()
  
  if (isAuthenticated) {
    return <Navigate to="/projects" replace />
  }
  
  return <>{children}</>
}

function App() {
  return (
    <div className="min-h-screen flex flex-col bg-mist">
      <Header />

      <main className="flex-1">
        <AppRoutes />
      </main>

      <ConditionalFooter />
    </div>
  )
}

const AppRoutes = () => {
  return (
    <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/projects" 
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/workspace/:projectId" 
            element={
              <ProtectedRoute>
                <WorkspacePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  )
}

/**
 * Hide the global footer on `/workspace/*` so it doesn't compete with
 * the workspace's own RoundSubmitBar / ScrollNav / sticky panels.
 * Everywhere else (landing, projects list, login, analytics) keeps it.
 */
const ConditionalFooter = () => {
  const { pathname } = useLocation()
  if (pathname.startsWith('/workspace')) return null
  return <Footer />
}

export default App
